import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
  CardActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import useBooleanInput from '../../hooks/useBooleanInput';
import { updateCancellations } from '../../shared/services/netServices';
import CancellationEmailDetailsForm from './CancellationEmailDetailsForm';

function CancellationEmailDetailsCard({ record = {} }) {
  const refresh = useRefresh();
  const notify = useNotify();

  const [hasError, setHasError] = useState({});
  const [amounts, setAmounts] = useState({});
  const [updateDisabled, setUpdateDisabled] = useBooleanInput(true);
  const [shouldSendEmail, setShouldSendEmail] = useState(
    record.cancellation_assignments[0].should_send_confirmation_email
  );

  function handleChange(event) {
    setUpdateDisabled(false);
    setHasError({});

    const { value: input, name, id } = event.target;
    let columnName = id.replaceAll('-', '_');

    const decimalPattern = /^-?\d+(\.\d{1,2})?$/;

    const valid = decimalPattern.test(input);

    const updateAmounts = {
      ...amounts,
      [name]: {
        ...amounts[name],
        [columnName]: input,
        cancellation_assignment_id: name,
        should_send_confirmation_email: shouldSendEmail,
      },
    };

    if (columnName === 'prevent_refund' || columnName === 'wellness_refund') {
      updateAmounts[name]['outstanding_balance'] = '0.00';
    } else if (columnName === 'outstanding_balance') {
      updateAmounts[name]['prevent_refund'] = '0.00';
      updateAmounts[name]['wellness_refund'] = '0.00';
    }
    setAmounts(updateAmounts);

    setHasError({
      [name]: {
        [id]: !valid,
      },
    });
  }

  function canUpdate() {
    let canUpdate = true;
    Object.keys(hasError).forEach(function (name) {
      Object.keys(hasError[name]).forEach(function (id) {
        if (hasError[name][id]) {
          canUpdate = false;
        }
      });
    });
    return canUpdate;
  }

  function handleUpdate() {
    let payload = transformAmount(amounts);

    if (Object.keys(payload).length === 0) {
      const cancellationAssignmentId = record.cancellation_assignments[0].id;

      payload = {
        ...amounts,
        [cancellationAssignmentId]: {
          ...amounts[cancellationAssignmentId],
          ['cancellation_assignment_id']: cancellationAssignmentId,
          ['should_send_confirmation_email']: shouldSendEmail,
        },
      };
    }

    if (canUpdate()) {
      updateCancellations(Object.values(payload))
        .then(() => {
          refresh();
          setUpdateDisabled(true);
          notify('Cancellation email details updated', {});
        })
        .catch(() => {
          notify('Cancellation email details not updated', 'warning');
        });
    } else {
      notify('Fix fields with error before updating', 'warning');
    }
  }

  function transformAmount(inputObject) {
    let transformedObject = {};

    for (const key in inputObject) {
      if (Object.hasOwn(inputObject, key)) {
        const child = inputObject[key];
        let transformedChild = {
          cancellation_assignment_id: child['cancellation_assignment_id'],
          should_send_confirmation_email: child['should_send_confirmation_email'],
        };

        // Check outstanding_balance and prevent_refund conditions
        if (parseFloat(child['outstanding_balance']) >= 0) {
          transformedChild['outstanding_balance'] = child['outstanding_balance'];
        }
        // Check insurance_refund conditions
        if (child['insurance_refund']) {
          transformedChild['insurance_refund'] = child['insurance_refund'];
        }
        // if prevent_refund or wellness_refund is greater than 0,
        // set outstanding_balance to negative value
        if (parseFloat(child['prevent_refund']) > 0) {
          transformedChild['outstanding_balance'] = `-${child['prevent_refund']}`;
        }

        if (parseFloat(child['wellness_refund']) > 0) {
          transformedChild['outstanding_balance'] = `-${child['wellness_refund']}`;
        }
        transformedObject[key] = transformedChild;
      }
    }

    return transformedObject;
  }

  function onEmailOptionChange(e) {
    setUpdateDisabled(false);
    setShouldSendEmail(e.target.value === 'true');
  }

  return (
    <div style={{ marginBottom: '32px' }}>
      <Typography variant='h5'>3. Send Email</Typography>

      <Card>
        <CardContent>
          {record.cancellation_assignments.map(function (cancellation) {
            return (
              <CancellationEmailDetailsForm
                key={cancellation.id}
                cancellation={cancellation}
                amounts={amounts[cancellation.id]}
                hasError={hasError}
                handleChange={handleChange}
              />
            );
          })}
        </CardContent>

        {Object.keys(record).length > 0 ? (
          <FormControl component='fieldset' sx={{ marginLeft: '16px' }}>
            <FormLabel component='legend'>Send Email?</FormLabel>

            <RadioGroup
              sx={{ marginLeft: '16px' }}
              value={shouldSendEmail}
              onChange={e => onEmailOptionChange(e)}
            >
              <FormControlLabel
                value={true}
                control={<Radio color='primary' />}
                label='Yes'
              />

              <FormControlLabel
                value={false}
                control={<Radio color='primary' />}
                label='No'
              />
            </RadioGroup>
          </FormControl>
        ) : null}

        <CardActions>
          <Button
            onClick={handleUpdate}
            variant='contained'
            disabled={updateDisabled}
            style={{
              marginLeft: '8px',
              marginBottom: '8px',
            }}
          >
            Update
          </Button>
        </CardActions>
      </Card>
    </div>
  );
}

CancellationEmailDetailsCard.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string,
    cancellation_assignments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        should_send_confirmation_email: PropTypes.bool,
      })
    ),
  }),
};

export default CancellationEmailDetailsCard;
